import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { format, isToday, isTomorrow, isPast } from 'date-fns';
import toast from 'react-hot-toast';
import { API_URL } from '../utils/config';
import useStore from '../store/useStore';
import useAuth from '../hooks/useAuth';

const api = async (path, options = {}) => {
  const token = localStorage.getItem('qtalk_token');
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...(options.headers||{}) }
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

const priorityConfig = {
  high:   { label:'High',   color:'#ef4444', bg:'rgba(239,68,68,0.1)',   border:'rgba(239,68,68,0.2)'   },
  medium: { label:'Medium', color:'#f59e0b', bg:'rgba(245,158,11,0.1)',  border:'rgba(245,158,11,0.2)'  },
  low:    { label:'Low',    color:'#10b981', bg:'rgba(16,185,129,0.1)',  border:'rgba(16,185,129,0.2)'  },
};

const statusConfig = {
  pending:     { label:'To Do',       color:'#6b7280', bg:'rgba(107,114,128,0.1)', icon:'○' },
  in_progress: { label:'In Progress', color:'#3b82f6', bg:'rgba(59,130,246,0.1)',  icon:'◑' },
  qa:          { label:'QA',          color:'#a855f7', bg:'rgba(168,85,247,0.1)',  icon:'⚙' },
  uat:         { label:'UAT',         color:'#ec4899', bg:'rgba(236,72,153,0.1)',  icon:'⚑' },
  prod:        { label:'PROD',        color:'#f97316', bg:'rgba(249,115,22,0.1)',  icon:'▲' },
  done:        { label:'Done',        color:'#10b981', bg:'rgba(16,185,129,0.1)',  icon:'✓' },
};

// ── Searchable Assignee Selector with Portal
function AssigneeSelector({ users, value, onChange, darkMode }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState({top:0,left:0,width:300});
  const triggerRef = useRef(null);
  const selected = users.find(u => String(u.id) === String(value));
  const filtered = users.filter(u =>
    u.full_name.toLowerCase().includes(search.toLowerCase()) ||
    (u.email||'').toLowerCase().includes(search.toLowerCase()) ||
    (u.department||'').toLowerCase().includes(search.toLowerCase())
  );

  const handleOpen = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      // Check if dropdown would go off bottom of screen
      const spaceBelow = window.innerHeight - r.bottom;
      const dropH = Math.min(users.length * 52 + 80, 300);
      setPos({
        top: spaceBelow > dropH ? r.bottom + 4 : r.top - dropH - 4,
        left: r.left,
        width: r.width
      });
    }
    setOpen(p => !p);
    setSearch('');
  };

  const DropdownContent = () => (
    <>
      <div style={{position:'fixed',inset:0,zIndex:9998}} onClick={() => setOpen(false)}/>
      <div style={{
        position:'fixed',
        top: pos.top,
        left: pos.left,
        width: pos.width,
        zIndex: 9999,
        background: darkMode ? '#1e2d3d' : 'white',
        border: `1.5px solid ${darkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        overflow: 'hidden',
        maxHeight: '300px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Search bar */}
        <div style={{padding:'8px', borderBottom:`1px solid ${darkMode?'rgba(255,255,255,0.06)':'#f1f5f9'}`, flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',gap:'8px',padding:'8px 12px',borderRadius:'10px',background:darkMode?'rgba(255,255,255,0.06)':'#f8fafc'}}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14,color:'#9ca3af',flexShrink:0}}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input type="text" value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search people..." autoFocus
              style={{flex:1,background:'transparent',border:'none',outline:'none',fontSize:'13px',color:darkMode?'white':'#0f172a'}}/>
          </div>
        </div>

        {/* List */}
        <div style={{overflowY:'auto',flex:1}}>
          {/* Unassigned */}
          <button type="button" onClick={() => { onChange(''); setOpen(false); }}
            style={{width:'100%',display:'flex',alignItems:'center',gap:'10px',padding:'10px 16px',
              background: !value ? 'rgba(13,148,136,0.08)' : 'transparent',
              border:'none',cursor:'pointer',textAlign:'left',
              color: !value ? '#0d9488' : darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280'}}>
            <div style={{width:28,height:28,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,background:darkMode?'rgba(255,255,255,0.08)':'#f1f5f9'}}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14,color:'#9ca3af'}}>
                <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
              </svg>
            </div>
            <span style={{fontSize:13,fontStyle:'italic',fontWeight:500}}>Unassigned</span>
            {!value && <svg viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14,marginLeft:'auto'}}><polyline points="20 6 9 17 4 12"/></svg>}
          </button>

          {filtered.map(u => (
            <button key={u.id} type="button" onClick={() => { onChange(String(u.id)); setOpen(false); }}
              style={{width:'100%',display:'flex',alignItems:'center',gap:'10px',padding:'8px 16px',
                background: String(value)===String(u.id) ? 'rgba(13,148,136,0.08)' : 'transparent',
                border:'none',cursor:'pointer',textAlign:'left'}}
              onMouseEnter={e=>{e.currentTarget.style.background=darkMode?'rgba(255,255,255,0.05)':'#f0fdf4'}}
              onMouseLeave={e=>{e.currentTarget.style.background=String(value)===String(u.id)?'rgba(13,148,136,0.08)':'transparent'}}>
              <div style={{width:32,height:32,borderRadius:'50%',background:'linear-gradient(135deg,#0d9488,#2563eb)',display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontSize:12,fontWeight:'bold',flexShrink:0,overflow:'hidden'}}>
                {u.avatar_url?.startsWith('https://')
                  ? <img src={u.avatar_url?.startsWith("https://") ? u.avatar_url : `https://qtalk.qualtechedge.in${u.avatar_url}`} style={{width:'100%',height:'100%',objectFit:'cover'}} alt=""/>
                  : u.full_name?.[0]?.toUpperCase()}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <p style={{fontSize:13,fontWeight:600,color:darkMode?'white':'#0f172a',margin:0,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{u.full_name}{u._isSelf ? ' (You)' : ''}</p>
                <p style={{fontSize:11,color:'#9ca3af',margin:0,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{u.designation||u.department||u.email}</p>
              </div>
              {String(value)===String(u.id) && (
                <svg viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14,flexShrink:0}}><polyline points="20 6 9 17 4 12"/></svg>
              )}
            </button>
          ))}
          {filtered.length===0 && search && (
            <p style={{textAlign:'center',fontSize:12,color:'#9ca3af',padding:'16px'}}>No users found</p>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div>
      <button ref={triggerRef} type="button" onClick={handleOpen}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-left transition focus:outline-none focus:ring-2 focus:ring-teal-400"
        style={{
          background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
          border: `1.5px solid ${open ? '#0d9488' : darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
          color: darkMode ? 'white' : '#0f172a'
        }}>
        {selected ? (
          <>
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
              {selected.avatar_url?.startsWith('https://')
                ? <img src={selected.avatar_url?.startsWith("https://") ? selected.avatar_url : `https://qtalk.qualtechedge.in${selected.avatar_url}`} className="w-full h-full object-cover" alt=""/>
                : selected.full_name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate" style={{color:darkMode?'white':'#0f172a'}}>{selected.full_name}</p>
              <p className="text-xs truncate" style={{color:'#9ca3af'}}>{selected.designation||selected.department||''}</p>
            </div>
          </>
        ) : (
          <>
            <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
              style={{background: darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-gray-400">
                <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
              </svg>
            </div>
            <span className="flex-1" style={{color:'#9ca3af'}}>— Unassigned —</span>
          </>
        )}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          className={`w-4 h-4 flex-shrink-0 transition-transform ${open?'rotate-180':''}`}
          style={{color:'#9ca3af'}}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && ReactDOM.createPortal(<DropdownContent/>, document.body)}
    </div>
  );
}

export default function TodoPage() {
  const { user, darkMode } = useStore();
  const { ready } = useAuth();
  const [todos, setTodos] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editTodo, setEditTodo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title:'', description:'', priority:'medium', due_date:'', assigned_to:'' });

  useEffect(() => { if (ready) { fetchTodos(); fetchUsers(); } }, [ready]);

  const fetchTodos = async () => {
    try { const data = await api('/todos'); setTodos(data); }
    catch(e) { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  };
  const fetchUsers = async () => {
    try {
      const data = await api('/users');
      console.log('Users fetched:', data?.length, data);
      const list = Array.isArray(data) ? data : [];
      // Add current user at top so they can assign tasks to themselves
      const currentUser = user ? { ...user, _isSelf: true } : null;
      const alreadyIncluded = currentUser && list.find(u => u.id === currentUser.id);
      setUsers(currentUser && !alreadyIncluded ? [currentUser, ...list] : list);
    } catch(e) { console.error('fetchUsers failed:', e); }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Title required');
    try {
      if (editTodo) { await api(`/todos/${editTodo.id}`, { method:'PUT', body: JSON.stringify(form) }); toast.success('Task updated!'); }
      else { await api('/todos', { method:'POST', body: JSON.stringify(form) }); toast.success('Task created!'); }
      setShowForm(false); setEditTodo(null);
      setForm({ title:'', description:'', priority:'medium', due_date:'', assigned_to:'' });
      fetchTodos();
    } catch(e) { toast.error('Failed'); }
  };

  const updateStatus = async (id, status) => {
    try { await api(`/todos/${id}`, { method:'PUT', body: JSON.stringify({ status }) }); fetchTodos(); } catch(e) {}
  };

  const deleteTodo = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try { await api(`/todos/${id}`, { method:'DELETE' }); toast.success('Deleted'); fetchTodos(); } catch(e) {}
  };

  const startEdit = (todo) => {
    setEditTodo(todo);
    setForm({ title:todo.title, description:todo.description||'', priority:todo.priority, due_date:todo.due_date?todo.due_date.slice(0,16):'', assigned_to:todo.assigned_to||'' });
    setShowForm(true);
  };

  const getDue = (due) => {
    if (!due) return null;
    const d = new Date(due);
    if (isPast(d) && !isToday(d)) return { label:'Overdue', color:'#ef4444' };
    if (isToday(d))    return { label:'Today',    color:'#f59e0b' };
    if (isTomorrow(d)) return { label:'Tomorrow', color:'#3b82f6' };
    return { label:format(d,'MMM d'), color:'#6b7280' };
  };

  const [taskSearch, setTaskSearch] = useState('');
  const filtered = todos.filter(t => {
    // Search by ID or title
    if (taskSearch.trim()) {
      const q = taskSearch.trim().toLowerCase();
      const matchId = String(t.id) === q.replace('#','');
      const matchTitle = t.title.toLowerCase().includes(q);
      if (!matchId && !matchTitle) return false;
    }
    if (filter==='mine')     return t.user_id===user?.id;
    if (filter==='assigned') return t.assigned_to===user?.id && t.user_id!==user?.id;
    if (filter==='pending')  return t.status==='pending';
    if (filter==='progress') return t.status==='in_progress';
    if (filter==='qa')       return t.status==='qa';
    if (filter==='uat')      return t.status==='uat';
    if (filter==='prod')     return t.status==='prod';
    if (filter==='done')     return t.status==='done';
    if (filter==='high')     return t.priority==='high';
    return true;
  });

  const counts = {
    all: todos.length,
    pending: todos.filter(t=>t.status==='pending').length,
    progress: todos.filter(t=>t.status==='in_progress').length,
    qa: todos.filter(t=>t.status==='qa').length,
    uat: todos.filter(t=>t.status==='uat').length,
    prod: todos.filter(t=>t.status==='prod').length,
    done: todos.filter(t=>t.status==='done').length,
    high: todos.filter(t=>t.priority==='high').length,
  };

  const completion = counts.all > 0 ? Math.round((counts.done / counts.all) * 100) : 0;

  if (!ready || loading) return (
    <div className={`fixed inset-0 flex items-center justify-center ${darkMode?'dark bg-gray-950':'bg-gray-50'}`}>
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
          </svg>
        </div>
        <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"/>
      </div>
    </div>
  );

  return (
    <div className={`fixed inset-0 flex flex-col ${darkMode?'dark':''}`}
      style={{background: darkMode ? '#0a0f1a' : '#f1f5f9'}}>

      {/* ── HEADER ── */}
      <div className="flex-shrink-0 px-6 py-3 flex items-center justify-between"
        style={{background: darkMode ? 'rgba(15,23,42,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter:'blur(12px)', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'}}>
        <div className="flex items-center gap-3">
          <a href="/chat" className="w-8 h-8 rounded-xl flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5 transition">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          </a>
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
            </svg>
          </div>
          <div>
            <p className="font-bold text-gray-900 dark:text-white text-sm leading-tight">Task Manager</p>
            <p className="text-xs text-gray-400 leading-tight">QualTechEdge · Qtalk</p>
          </div>
        </div>
        <button onClick={() => { setShowForm(true); setEditTodo(null); setForm({title:'',description:'',priority:'medium',due_date:'',assigned_to:''}); }}
          className="flex items-center gap-2 px-4 py-2 text-white text-sm font-bold rounded-xl hover:opacity-90 transition shadow-lg"
          style={{background:'linear-gradient(135deg,#0d9488,#2563eb)', boxShadow:'0 4px 12px rgba(13,148,136,0.3)'}}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Task
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-6">

          {/* ── STATS ── */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[
              { label:'Total Tasks',  value:counts.all,      gradient:'135deg,#0d9488,#0369a1', icon:<path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/> },
              { label:'To Do',        value:counts.pending,  gradient:'135deg,#6b7280,#4b5563', icon:<circle cx="12" cy="12" r="10"/> },
              { label:'In Progress',  value:counts.progress, gradient:'135deg,#f59e0b,#d97706', icon:<><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></> },
              { label:'Completed',    value:counts.done,     gradient:'135deg,#10b981,#059669', icon:<><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></> },
            ].map(s => (
              <div key={s.label} className="relative rounded-2xl p-4 overflow-hidden text-white shadow-lg"
                style={{background:`linear-gradient(${s.gradient})`}}>
                <div className="absolute -right-4 -bottom-4 opacity-20">
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-20 h-20">{s.icon}</svg>
                </div>
                <p className="text-3xl font-black">{s.value}</p>
                <p className="text-white/70 text-xs font-medium mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          {/* ── PROGRESS BAR ── */}
          <div className="mb-6 rounded-2xl p-4"
            style={{background: darkMode ? 'rgba(255,255,255,0.04)' : 'white',
              border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'}}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-gray-700 dark:text-gray-200">Overall Progress</span>
              <span className="text-sm font-black" style={{color:'#0d9488'}}>{completion}%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{background: darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}}>
              <div className="h-full rounded-full transition-all duration-700"
                style={{width:`${completion}%`, background:'linear-gradient(90deg,#0d9488,#2563eb)'}}/>
            </div>
            {counts.high > 0 && (
              <p className="text-xs text-red-500 mt-2 font-semibold">⚠ {counts.high} high priority task{counts.high>1?'s':''} need attention</p>
            )}
          </div>

          {/* ── SEARCH BAR ── */}
          <div className="relative mb-4">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              className="absolute left-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input type="text" placeholder="Search by task ID (e.g. #12) or title..." value={taskSearch}
              onChange={e => setTaskSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
              style={{background: darkMode?'rgba(255,255,255,0.04)':'white', border: darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(0,0,0,0.08)', color: darkMode?'white':'#111'}}/>
            {taskSearch && (
              <button onClick={() => setTaskSearch('')} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 transition">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>
          {/* ── FILTER TABS ── */}
          <div className="flex gap-2 mb-5 flex-wrap">
            {[
              {id:'all',     label:'All', count:counts.all, icon:'📋'},
              {id:'pending', label:'To Do', count:counts.pending, icon:'⏳'},
              {id:'progress',label:'In Progress', count:counts.progress, icon:'🔄'},
              {id:'qa',      label:'QA', count:counts.qa, icon:'⚙️'},
              {id:'uat',     label:'UAT', count:counts.uat, icon:'🚩'},
              {id:'prod',    label:'PROD', count:counts.prod, icon:'🚀'},
              {id:'done',    label:'Done', count:counts.done, icon:'✅'},
              {id:'high',    label:'High Priority', count:counts.high, icon:'🔴'},
              {id:'mine',    label:'Created by me', count:null, icon:'👤'},
              {id:'assigned',label:'Assigned to me', count:null, icon:'📌'},
            ].map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all"
                style={filter===f.id
                  ? {background:'linear-gradient(135deg,#0d9488,#2563eb)', color:'white', boxShadow:'0 2px 8px rgba(13,148,136,0.3)'}
                  : {color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280',
                     background: darkMode ? 'rgba(255,255,255,0.04)' : 'white',
                     border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'}}>
                <span>{f.icon}</span>
                <span>{f.label}</span>
                {f.count !== null && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                    style={{background: filter===f.id ? 'rgba(255,255,255,0.25)' : darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9',
                            color: filter===f.id ? 'white' : darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280'}}>
                    {f.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* ── TASK LIST ── */}
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="w-20 h-20 rounded-3xl flex items-center justify-center"
                style={{background:'linear-gradient(135deg,#0d9488,#2563eb)', opacity:0.2}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
                  <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                </svg>
              </div>
              <div className="text-center">
                <p className="font-bold text-gray-500 dark:text-gray-400">No tasks here</p>
                <p className="text-sm text-gray-400 dark:text-gray-600 mt-1">Create a new task to get started</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(todo => {
                const due = getDue(todo.due_date);
                const pri = priorityConfig[todo.priority];
                const stat = statusConfig[todo.status];
                const isDone = todo.status === 'done';
                return (
                  <div key={todo.id}
                    className="group rounded-2xl p-4 transition-all hover:shadow-lg"
                    style={{
                      background: darkMode ? 'rgba(255,255,255,0.03)' : 'white',
                      border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                      opacity: isDone ? 0.65 : 1,
                      borderLeft: `3px solid ${pri.color}`
                    }}>
                    <div className="flex items-start gap-4">

                      {/* Status selector */}
                      <select value={todo.status} onChange={e => updateStatus(todo.id, e.target.value)}
                        className="text-xs font-bold px-2.5 py-1.5 rounded-xl border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-400 flex-shrink-0 transition"
                        style={{background:stat.bg, color:stat.color, minWidth:'100px'}}>
                        <option value="pending">○ To Do</option>
                        <option value="in_progress">◑ In Progress</option>
                        <option value="qa">⚙ QA</option>
                        <option value="uat">⚑ UAT</option>
                        <option value="prod">▲ PROD</option>
                        <option value="done">✓ Done</option>
                      </select>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-mono font-bold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded flex-shrink-0">
                            #{todo.id}
                          </span>
                          <p className={`font-bold text-sm text-gray-900 dark:text-white ${isDone?'line-through opacity-50':''}`}>
                            {todo.title}
                          </p>
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                            style={{background:pri.bg, color:pri.color, border:`1px solid ${pri.border}`}}>
                            {pri.label}
                          </span>
                        </div>
                        {todo.description && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 leading-relaxed">{todo.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          {due && (
                            <span className="flex items-center gap-1 text-xs font-semibold" style={{color:due.color}}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                              {due.label}
                            </span>
                          )}
                          {todo.due_date && !['Today','Tomorrow','Overdue'].includes(due?.label) && (
                            <span className="text-xs text-gray-400">{format(new Date(todo.due_date),'MMM d, HH:mm')}</span>
                          )}
                          {todo.assignee_name && (
                            <span className="flex items-center gap-1 text-xs text-gray-400">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                              {todo.assigned_to === user?.id ? 'Assigned to you' : todo.assignee_name}
                            </span>
                          )}
                          <span className="text-xs text-gray-300 dark:text-gray-700">Created by {todo.creator_name}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      {todo.user_id === user?.id && (
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={() => startEdit(todo)}
                            className="w-8 h-8 rounded-xl flex items-center justify-center text-gray-400 hover:text-teal-500 transition"
                            style={{background: darkMode ? 'rgba(255,255,255,0.05)' : '#f3f4f6'}}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button onClick={() => deleteTodo(todo.id)}
                            className="w-8 h-8 rounded-xl flex items-center justify-center text-gray-400 hover:text-red-500 transition"
                            style={{background: darkMode ? 'rgba(255,255,255,0.05)' : '#f3f4f6'}}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                              <path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── NEW/EDIT TASK MODAL ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl"
            style={{background: darkMode ? '#0f172a' : 'white',
              border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)'}}
            onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div className="px-6 py-5 flex items-center justify-between"
              style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    {editTodo ? <><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></> : <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>}
                  </svg>
                </div>
                <h3 className="font-black text-white text-base">{editTodo ? 'Edit Task' : 'New Task'}</h3>
              </div>
              <button onClick={() => setShowForm(false)}
                className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center text-white transition">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <form onSubmit={submitForm} className="px-6 py-5 space-y-4">
              {/* Title */}
              <div>
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest block mb-1.5">Task Title *</label>
                <input type="text" value={form.title} onChange={e=>setForm({...form,title:e.target.value})}
                  placeholder="What needs to be done?" autoFocus
                  className="w-full px-4 py-3 rounded-2xl text-sm font-medium focus:outline-none transition"
                  style={{
                    background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                    border: `1.5px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                    color: darkMode ? 'white' : '#0f172a'
                  }}/>
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest block mb-1.5">Description</label>
                <textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})}
                  placeholder="Add details, context, or notes..." rows={3}
                  className="w-full px-4 py-3 rounded-2xl text-sm focus:outline-none transition resize-none"
                  style={{
                    background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                    border: `1.5px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                    color: darkMode ? 'white' : '#0f172a'
                  }}/>
              </div>

              <div className="flex flex-col gap-3">
                {/* Priority */}
                <div>
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest block mb-1.5">Priority</label>
                  <div className="flex gap-1.5">
                    {['high','medium','low'].map(p => (
                      <button key={p} type="button" onClick={() => setForm({...form,priority:p})}
                        className="flex-1 py-2 rounded-xl text-xs font-bold transition"
                        style={{
                          background: form.priority===p ? priorityConfig[p].bg : darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                          border: `1.5px solid ${form.priority===p ? priorityConfig[p].color : 'transparent'}`,
                          color: form.priority===p ? priorityConfig[p].color : '#6b7280'
                        }}>
                        {p.charAt(0).toUpperCase()+p.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Due date + time */}
                <div>
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest block mb-1.5">Due Date & Time</label>
                  <div className="flex gap-2">
                    <input type="date"
                      value={form.due_date ? form.due_date.slice(0,10) : ''}
                      onChange={e => {
                        const date = e.target.value;
                        const time = form.due_date ? form.due_date.slice(11,16) : '09:00';
                        setForm({...form, due_date: date ? `${date}T${time}` : ''});
                      }}
                      className="flex-1 px-3 py-2.5 rounded-2xl text-sm focus:outline-none transition"
                      style={{
                        background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                        border: `1.5px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                        color: darkMode ? 'white' : '#0f172a',
                        colorScheme: darkMode ? 'dark' : 'light'
                      }}/>
                    <select
                      value={form.due_date ? form.due_date.slice(11,16) : '09:00'}
                      onChange={e => {
                        const date = form.due_date ? form.due_date.slice(0,10) : new Date().toISOString().slice(0,10);
                        setForm({...form, due_date: `${date}T${e.target.value}`});
                      }}
                      className="px-3 py-2.5 rounded-2xl text-sm focus:outline-none transition"
                      style={{
                        background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                        border: `1.5px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                        color: darkMode ? 'white' : '#0f172a',
                        minWidth: '110px',
                        flexShrink: 0
                      }}>
                      {Array.from({length:24*4}, (_,i) => {
                        const h = String(Math.floor(i/4)).padStart(2,'0');
                        const m = String((i%4)*15).padStart(2,'0');
                        return <option key={`${h}:${m}`} value={`${h}:${m}`}>{`${h}:${m}`}</option>;
                      })}
                    </select>
                  </div>
                  {form.due_date && (
                    <button type="button" onClick={() => setForm({...form,due_date:''})}
                      className="text-xs text-red-400 hover:text-red-500 mt-1 transition">
                      × Clear date
                    </button>
                  )}
                </div>
              </div>

              {/* Assign to — searchable */}
              <div className="relative">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest block mb-1.5">Assign To</label>
                <AssigneeSelector
                  users={users}
                  value={form.assigned_to}
                  onChange={val => setForm({...form, assigned_to: val})}
                  darkMode={darkMode}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 py-3 rounded-2xl text-sm font-bold transition"
                  style={{background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
                    color: darkMode ? '#9ca3af' : '#6b7280'}}>
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 py-3 rounded-2xl text-sm font-black text-white transition hover:opacity-90 shadow-lg"
                  style={{background:'linear-gradient(135deg,#0d9488,#2563eb)', boxShadow:'0 4px 12px rgba(13,148,136,0.3)'}}>
                  {editTodo ? 'Save Changes' : 'Create Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
